import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { errorMessage, useNotify } from '../../lib/notify';
import { contactKeys } from '../contacts/model';
import type { Paper } from '../sales/print/usePrint';
import {
  useSendWhatsapp,
  useWhatsapp,
  useWhatsappMessages,
  type WhatsappDocumentType,
} from './api';
import { fillTemplate, type MessageVars } from './messages';
import { usePdf } from './pdf';
import { useShop } from '../../shop/ShopProvider';

/** WhatsApp's own green (buttons only; text stays in theme colours). */
export const WHATSAPP_GREEN = '#1DA851';

export interface WhatsappTarget {
  documentType: WhatsappDocumentType;
  documentId: string;
  /** The document number, shown in the history on the contact. */
  documentRef?: string;
  contact: { id: string; name: string; phone?: string | null; isWalkIn?: boolean } | null;
  /** Fills the shop's message for this kind of document ({name}, {number}, …); the user can still change it. */
  vars: MessageVars;
  /** The PDF to attach (the document's print layout). */
  pdf?: { fileName: string; paper: Paper; render: () => ReactNode };
}

/**
 * The green WhatsApp button of a document, and its send dialog: phone (from the contact, or the
 * last number used for this document), message, and the document as a PDF. Hidden unless the
 * shop has the WhatsApp plugin switched on and the user may send. A walk-in sale asks for the
 * customer's name and number; with a name, a new customer is saved for the message.
 */
export function WhatsappSend({
  target,
  variant = 'button',
  label = 'WhatsApp',
  fullWidth,
  defaultOpen = false,
}: {
  target: WhatsappTarget;
  variant?: 'button' | 'icon';
  label?: string;
  fullWidth?: boolean;
  /** Open the send dialog straight away (e.g. coming from “Send ledger”). */
  defaultOpen?: boolean;
}) {
  const wa = useWhatsapp();
  const [open, setOpen] = useState(defaultOpen);
  if (!wa.canSend || !wa.isOn(target.documentType)) return null;
  const button =
    variant === 'icon' ? (
      <Tooltip title="Send on WhatsApp">
        <IconButton
          aria-label="Send on WhatsApp"
          onClick={() => setOpen(true)}
          sx={{ color: WHATSAPP_GREEN }}
        >
          <WhatsAppIcon />
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        variant="contained"
        startIcon={<WhatsAppIcon />}
        onClick={() => setOpen(true)}
        fullWidth={fullWidth}
        sx={{
          bgcolor: WHATSAPP_GREEN,
          color: '#fff',
          '&:hover': { bgcolor: '#178A43' },
        }}
      >
        {label}
      </Button>
    );
  return (
    <>
      {button}
      {open &&
        (wa.expired ? (
          <ExpiredDialog onClose={() => setOpen(false)} />
        ) : (
          <SendDialog
            target={target}
            template={wa.template(target.documentType)}
            linked={wa.linked}
            onClose={() => setOpen(false)}
          />
        ))}
    </>
  );
}

function ExpiredDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>WhatsApp subscription ended</DialogTitle>
      <DialogContent>
        <Typography>
          WhatsApp subscription ended, grace used. Contact PackBytes to renew it.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          component={RouterLink}
          to="/support?message=Hi%2C%20I%20want%20to%20renew%20WhatsApp"
        >
          Contact support
        </Button>
        <Button variant="contained" onClick={onClose}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SendDialog({
  target,
  template,
  linked,
  onClose,
}: {
  target: WhatsappTarget;
  template: string;
  linked: boolean;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const notify = useNotify();
  const qc = useQueryClient();
  const send = useSendWhatsapp();
  const pdf = usePdf();
  // Documents carry only the contact's id, name and phone: look it up to know if it is walk-in.
  const contactId0 = target.contact?.id ?? '';
  const contact = useQuery({
    queryKey: contactKeys.detail(contactId0),
    queryFn: () => unwrap(api.GET('/contacts/{id}', { params: { path: { id: contactId0 } } })),
    enabled: !!target.contact && target.contact.isWalkIn === undefined,
  });
  const walkIn = !target.contact || (target.contact.isWalkIn ?? contact.data?.isWalkIn ?? false);
  // The customer saved from a walk-in name, kept so a retry does not save them twice.
  const savedCustomer = useRef<{ id: string; name: string } | null>(null);
  const history = useWhatsappMessages({
    documentType: target.documentType,
    documentId: target.documentId,
  });
  const lastPhone = history.data?.data[0]?.phone;
  const [phone, setPhone] = useState<string | null>(null);
  const [name, setName] = useState('');
  const { settings } = useShop();
  const [message, setMessage] = useState(() =>
    fillTemplate(template, { shop: settings?.name, ...target.vars }),
  );
  const [attach, setAttach] = useState(!!target.pdf);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = useRef(crypto.randomUUID());
  // Typed number, else the contact's, else the last one used for this document.
  const phoneValue = phone ?? ((walkIn ? null : target.contact?.phone) || lastPhone || '');

  const submit = async () => {
    setError(null);
    if (!phoneValue.trim()) {
      setError('Enter the WhatsApp number.');
      return;
    }
    setBusy(true);
    try {
      let contactId = walkIn ? undefined : target.contact?.id;
      let recipientName = walkIn ? name.trim() || undefined : target.contact?.name;
      // Walk-in with a name: keep them as a customer, so the message shows on their page.
      if (walkIn && name.trim() && can('customers.create')) {
        const created =
          savedCustomer.current ??
          (await unwrap(
            api.POST('/contacts', {
              params: { header: { 'idempotency-key': crypto.randomUUID() } },
              body: {
                name: name.trim(),
                phone: phoneValue.trim(),
                isCustomer: true,
                isSupplier: false,
              },
            }),
          ));
        savedCustomer.current = created;
        contactId = created.id;
        recipientName = created.name;
        await qc.invalidateQueries({ queryKey: ['contacts'] });
      }
      const attachment =
        attach && target.pdf
          ? {
              fileName: target.pdf.fileName,
              contentBase64: await pdf.make(target.pdf.paper, target.pdf.render()),
            }
          : undefined;
      await send.mutateAsync({
        id: id.current,
        phone: phoneValue.trim(),
        message: message.trim(),
        documentType: target.documentType,
        documentId: target.documentId,
        ...(target.documentRef ? { documentRef: target.documentRef } : {}),
        ...(contactId ? { contactId } : {}),
        ...(recipientName ? { recipientName } : {}),
        ...(attachment ? { attachment } : {}),
      });
      notify.success('Sent on WhatsApp');
      onClose();
    } catch (e) {
      // A new id for the next try (a failed send is logged under this one).
      id.current = crypto.randomUUID();
      setError(e instanceof ApiError ? e.message : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WhatsAppIcon sx={{ color: WHATSAPP_GREEN }} />
        Send on WhatsApp
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!linked && (
            <Alert severity="warning">
              The shop’s WhatsApp number is not linked yet.{' '}
              {can('whatsapp.manage') ? (
                <RouterLink to="/settings?tab=whatsapp">Link it in Settings → WhatsApp.</RouterLink>
              ) : (
                'Ask the owner to link it in Settings → WhatsApp.'
              )}
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {walkIn ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Walk-in sale: enter the customer’s number. Add a name to save them as a customer.
              </Typography>
              <TextField
                label="Customer name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!can('customers.create')}
                helperText={
                  can('customers.create')
                    ? undefined
                    : 'You cannot add customers; the number is kept on this message only.'
                }
              />
            </>
          ) : (
            <Typography variant="body2">
              To <strong>{target.contact?.name}</strong>
            </Typography>
          )}
          <TextField
            label="WhatsApp number"
            value={phoneValue}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="0300 1234567"
            slotProps={{ htmlInput: { inputMode: 'tel', autoComplete: 'tel' } }}
            required
            autoFocus={walkIn}
          />
          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            minRows={3}
            maxRows={10}
            slotProps={{ htmlInput: { maxLength: 4000 } }}
          />
          {target.pdf && (
            <FormControlLabel
              control={<Checkbox checked={attach} onChange={(e) => setAttach(e.target.checked)} />}
              label={`Attach ${target.pdf.fileName}`}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<WhatsAppIcon />}
          disabled={busy || !message.trim()}
          onClick={() => void submit()}
          sx={{ bgcolor: WHATSAPP_GREEN, '&:hover': { bgcolor: '#178A43' } }}
        >
          {busy ? (pdf.making ? 'Making the PDF…' : 'Sending…') : 'Send'}
        </Button>
      </DialogActions>
      {pdf.element}
    </Dialog>
  );
}
